import React, { useState, useEffect } from "react";
import { Modal, Input, Button, InputNumber, notification } from "antd";
import type { CriteriaSchemaItem } from "../../../../core/data/redux/recruitment/types";

interface CriteriaBuilderModalProps {
  visible: boolean;
  initialCriteria: CriteriaSchemaItem[];
  onSave: (criteria: CriteriaSchemaItem[]) => void;
  onCancel: () => void;
}

const CriteriaBuilderModal: React.FC<CriteriaBuilderModalProps> = ({
  visible,
  initialCriteria,
  onSave,
  onCancel,
}) => {
  const [criteria, setCriteria] = useState<CriteriaSchemaItem[]>([]);

  useEffect(() => {
    if (visible) {
      setCriteria(initialCriteria || []);
    }
  }, [visible, initialCriteria]);

  const handleAdd = () => {
    setCriteria([
      ...criteria,
      {
        key: `crit_${Date.now()}`,
        label: "",
        weight: 0,
        description: "",
      },
    ]);
  };

  const handleChange = (
    index: number,
    field: keyof CriteriaSchemaItem,
    value: any,
  ) => {
    const updated = [...criteria];
    updated[index] = { ...updated[index], [field]: value };
    // Automatically generate strict key if label is typed
    if (field === "label" && (!updated[index].key || updated[index].key.startsWith("crit_"))) {
      updated[index].key = value.toLowerCase().replace(/[^a-z0-9_]/g, "_").substring(0, 30);
    }
    setCriteria(updated);
  };

  const handleRemove = (index: number) => {
    const updated = [...criteria];
    updated.splice(index, 1);
    setCriteria(updated);
  };

  const handleSave = () => {
    // Validate that total weight equals 100
    const totalWeight = criteria.reduce((sum, c) => sum + (c.weight || 0), 0);
    
    // Ignore validation if list is empty (no criteria means it doesn't get evaluated, 
    // although backend requires weights to sum to 100 if criteria exist)
    if (criteria.length > 0 && Math.abs(totalWeight - 100) > 1) {
      notification.error({
        message: "Invalid Weights",
        description: `Total weight must be exactly 100. Current total is ${totalWeight}.`,
      });
      return;
    }

    if (criteria.some(c => !c.label || !c.key || c.weight <= 0)) {
      notification.error({
        message: "Incomplete Criteria",
        description: "All criteria must have a label, key, and weight > 0.",
      });
      return;
    }

    onSave(criteria);
  };

  const totalWeight = criteria.reduce((sum, c) => sum + (c.weight || 0), 0);
  const isValidWeight = Math.abs(totalWeight - 100) <= 1 || criteria.length === 0;

  return (
    <Modal
      title="Build Evaluation Criteria"
      open={visible}
      onCancel={onCancel}
      width={700}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={handleSave}
          disabled={criteria.length > 0 && !isValidWeight}
        >
          Save Criteria
        </Button>,
      ]}
    >
      <div className="mb-3">
        <p className="text-muted mb-2" style={{ fontSize: 13 }}>
          Define the rubrics for this round. The weights represent the relative importance 
          of each criterion and must sum to exactly 100.
        </p>
        <div
          className={`d-flex justify-content-between align-items-center p-2 rounded ${
            isValidWeight ? "bg-light text-success" : "bg-danger-light text-danger fw-bold"
          }`}
        >
          <span>Total Weight:</span>
          <span>{totalWeight} / 100</span>
        </div>
      </div>

      <div style={{ maxHeight: "50vh", overflowY: "auto", overflowX: "hidden" }} className="pe-2">
        {criteria.map((crit, index) => (
          <div key={index} className="p-3 mb-3 border rounded border-light bg-white shadow-sm position-relative">
            <Button
              type="text"
              danger
              icon={<i className="ti ti-trash" />}
              style={{ position: "absolute", top: 10, right: 10 }}
              onClick={() => handleRemove(index)}
            />
            
            <div className="row g-3">
              <div className="col-8">
                <label className="form-label" style={{ fontSize: 12 }}>Criterion Name / Label</label>
                <Input
                  placeholder="e.g. Technical Depth"
                  value={crit.label}
                  onChange={(e) => handleChange(index, "label", e.target.value)}
                />
              </div>
              <div className="col-4">
                <label className="form-label" style={{ fontSize: 12 }}>Weight (0-100)</label>
                <InputNumber
                  min={1}
                  max={100}
                  className="w-100"
                  value={crit.weight}
                  onChange={(val) => handleChange(index, "weight", val)}
                />
              </div>
              <div className="col-4">
                <label className="form-label" style={{ fontSize: 12 }}>System Key</label>
                <Input
                  placeholder="technical_depth"
                  value={crit.key}
                  disabled
                  title="Automatically generated from label"
                />
              </div>
              <div className="col-8">
                <label className="form-label" style={{ fontSize: 12 }}>Tooltip / Description</label>
                <Input
                  placeholder="What specifically to evaluate..."
                  value={crit.description}
                  onChange={(e) => handleChange(index, "description", e.target.value)}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <Button type="dashed" block icon={<i className="ti ti-plus" />} onClick={handleAdd} className="mt-2">
        Add Criterion
      </Button>
    </Modal>
  );
};

export default CriteriaBuilderModal;
